import { CheckboxGroup, Flex, FormControl } from '@chakra-ui/react';
import React from 'react';
import { Controller, get, useFormContext } from 'react-hook-form';

import CustomCheckbox from '@/common/CustomCheckboxGroup/CustomCheckbox/CustomCheckbox';
import CustomCheckboxWithInput from '@/common/CustomCheckboxGroup/CustomCheckboxWithInput/CustomCheckboxWithInput';
import CustomDescriptionText from '@/common/CustomDescriptionText/CustomDescriptionText';
import CustomErrorMessage from '@/common/CustomErrorMessage/CustomErrorMessage';
import CustomFormFieldLabel from '@/common/CustomFormFieldLabel/CustomFormFieldLabel';
import './CustomCheckboxGroup.scss';
import CustomShowFormValueOnly from '@/common/CustomShowFormValueOnly/CustomShowFormValueOnly';

interface CustomCheckboxGroupProps {
  name: string;
  label: string;
  description?: string;
  checkboxOptions: {
    name: string;
    label: string;
  }[];
  isRequired?: boolean;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  showFormValueOnly?: boolean;
  maxNumberOfSelections?: number;
}

const CustomCheckboxGroup = ({
  name,
  label,
  description,
  checkboxOptions,
  isRequired = false,
  isDisabled = false,
  isReadOnly = false,
  showFormValueOnly = false,
  maxNumberOfSelections,
}: CustomCheckboxGroupProps) => {
  const {
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext();
  const watchedValue = watch(name, []);

  // Check if the Others checkbox is selected + input not filled
  const othersIsCheckedButNotFilled = watchedValue.includes('');

  //////////////////////////////////////////
  // Limit max number of selections
  //////////////////////////////////////////

  // Disable checkbox if max number of selections allowed is reached
  const currentSelectedCount = watchedValue.filter(
    (value: boolean | string) => value !== false
  ).length;

  const disableNewSelectionsDueToMaxLimitReached = maxNumberOfSelections
    ? currentSelectedCount >= maxNumberOfSelections
    : false;

  const handleCheckboxChange = (index: number, value: string | boolean) => {
    const newValue = [...watchedValue];

    if (value === false) {
      newValue[index] = false;
    } else if (
      !maxNumberOfSelections ||
      currentSelectedCount < maxNumberOfSelections
    ) {
      newValue[index] = value;
    }
    setValue(name, newValue, { shouldValidate: true });
  };

  //////////////////////////////////////////
  // Render checkbox
  //////////////////////////////////////////

  const renderCheckbox = (
    option: { label: string; name: string },
    index: number,
    isChecked: boolean
  ) => {
    return (
      <CustomCheckbox
        label={option.label}
        value={option.label}
        onChange={newValue => handleCheckboxChange(index, newValue)}
        isChecked={isChecked}
        isRequired={isRequired}
        isDisabled={
          isDisabled || (!isChecked && disableNewSelectionsDueToMaxLimitReached)
        }
        isReadOnly={isReadOnly}
      />
    );
  };

  //////////////////////////////////////////
  // Render checkbox with input field
  //////////////////////////////////////////

  const renderCustomCheckboxWithInput = (
    option: {
      label: string;
      name: string;
    },
    index: number,
    isChecked: boolean
  ) => {
    return (
      <CustomCheckboxWithInput
        name={`${name}[${index}]`}
        label={option.label}
        onChange={newValue => handleCheckboxChange(index, newValue)}
        isChecked={isChecked}
        isRequired={isRequired}
        isDisabled={
          isDisabled ||
          (!isChecked &&
            disableNewSelectionsDueToMaxLimitReached &&
            !othersIsCheckedButNotFilled)
        }
        isReadOnly={isReadOnly}
        othersIsCheckedButNotFilled={othersIsCheckedButNotFilled}
      />
    );
  };

  //////////////////////////////////////////
  // Validation logic
  //////////////////////////////////////////

  const noCheckboxSelectedError = get(errors, name);
  const isErrorState =
    noCheckboxSelectedError &&
    !othersIsCheckedButNotFilled &&
    !isDisabled &&
    !isReadOnly;

  //////////////////////////////////////////
  // Show form value only
  //////////////////////////////////////////

  const inputValue = getValues(name);

  if (showFormValueOnly)
    return <CustomShowFormValueOnly inputValue={inputValue} />;

  //////////////////////////////////////////

  return (
    <Flex className={'custom-checkbox-group-container'}>
      <Flex className={'custom-checkbox-label-and-helper-text'}>
        <CustomFormFieldLabel
          label={label}
          isDisabled={isDisabled}
          isRequired={isRequired}
        />

        {description && <CustomDescriptionText description={description} />}
      </Flex>

      <Controller
        name={name}
        defaultValue={checkboxOptions.map(() => false)}
        render={({ field: { value, onChange } }) => (
          <>
            <FormControl
              isInvalid={isErrorState}
              className={`custom-form-control`}
            >
              <CheckboxGroup value={value.filter(Boolean)} onChange={onChange}>
                {checkboxOptions.map((option, index) => {
                  const isChecked = !!value[index];
                  const isOthersCheckbox = option.label
                    ?.toLowerCase()
                    .includes('others');

                  return (
                    <React.Fragment key={option.name}>
                      {!isOthersCheckbox &&
                        renderCheckbox(option, index, isChecked)}
                    </React.Fragment>
                  );
                })}
              </CheckboxGroup>
            </FormControl>

            <FormControl
              isInvalid={isErrorState}
              className={`custom-form-control`}
            >
              {checkboxOptions.map((option, index) => {
                const isChecked = !!value[index];
                const isOthersCheckbox = option.label
                  ?.toLowerCase()
                  .includes('others');

                return (
                  <React.Fragment key={option.name}>
                    {isOthersCheckbox &&
                      renderCustomCheckboxWithInput(option, index, isChecked)}
                  </React.Fragment>
                );
              })}
            </FormControl>
          </>
        )}
      />

      {isErrorState && (
        <CustomErrorMessage errorMessage={noCheckboxSelectedError?.message} />
      )}
    </Flex>
  );
};

export default CustomCheckboxGroup;
